/**
 * ctx/context.ts — `CardContext` и `PureContext` (tech/22 §4, D5/D31/S9).
 *
 * `ctx` собирается движком НА КАЖДЫЙ вызов хука. Он даёт: кто-я (self/effect/
 * round), чтение (q — read-only проекции), операции (ops — немедленные, D14/R1)
 * и RNG (только отсюда — D7). `choose` — веха M3b, здесь его НЕТ.
 *
 * Авто-`sourceId` (D31): ctx.ops.dealDamage без явного sourceId подставляет
 * ctx.self.id (юнит-носитель) либо ctx.effect.sourceId (контекст эффекта).
 * Авто-носитель setVar: self (unit) либо effect.
 *
 * `PureContext = Pick<CardContext,'self'|'effect'|'round'|'q'>` — урезанный
 * контекст для ЧИСТЫХ modify*, replace*-хуков (S9): БЕЗ ops/rng ⇒ вызов
 * ctx.ops.* из чистого хука не компилируется (закреплено ТИПОМ, не рантаймом).
 *
 * `self`/`effect` — СНИМКИ на момент сборки ctx (свежие проекции); текущие
 * ЭФФЕКТИВНЫЕ значения читаются через q (q.* читают живой черновик на момент
 * вызова).
 */
import { canonicalCellOrder, opposing } from '../state/board.js';
import { cardTypeOf } from '../cards/query.js';
import { requireInstance, requirePlayer } from '../state/access.js';
import { compareEffectsCanonical, effectiveStats } from '../stats/effectiveStats.js';
import { hasUnitAttackPermission, ignoresSummoningDelay } from '../combat/attackPermissions.js';
import { rngChance, rngInt, rngPick, rngShuffle } from '../rng/rng.js';
import { withLegacyPlanProjection } from '../targeting/planning.js';
import { toCellView, toEffectView, toPlayerView, toUnitView, } from './views.js';
/** Собрать read-only queries поверх живого черновика (q.* читают его на момент вызова). */
function buildQuery(draft, registry, completedAppearanceIds = new Set()) {
    const resolveId = (unit) => typeof unit === 'string' ? unit : unit.id;
    const cell = (cellId) => {
        const c = draft.board.cells[cellId];
        return c !== undefined ? toCellView(c) : null;
    };
    const unitAtId = (occupantId) => {
        if (occupantId === null)
            return null;
        const inst = draft.instances[occupantId];
        return inst !== undefined ? toUnitView(inst) : null;
    };
    return {
        instance(instanceId) {
            const inst = draft.instances[instanceId];
            return inst === undefined ? null : toUnitView(inst);
        },
        cardType(instanceId) {
            return registry === undefined ? undefined : cardTypeOf(draft, registry, instanceId);
        },
        cardTraits(instanceId) {
            if (registry === undefined)
                return [];
            const inst = draft.instances[instanceId];
            if (inst === undefined)
                return [];
            return registry.get(inst.defId)?.traits ?? [];
        },
        cell,
        opposingCell(cellId) {
            const c = draft.board.cells[cellId];
            if (c === undefined)
                return null;
            return cell(opposing(cellId));
        },
        unitAt(cellId) {
            const c = draft.board.cells[cellId];
            return c !== undefined ? unitAtId(c.occupantId) : null;
        },
        unitsOf(playerId) {
            requirePlayer(draft, playerId, 'q.unitsOf');
            const out = [];
            for (const id of canonicalCellOrder) {
                const c = draft.board.cells[id];
                if (c === undefined || c.occupantId === null)
                    continue;
                const inst = draft.instances[c.occupantId];
                if (inst !== undefined && inst.controllerId === playerId)
                    out.push(toUnitView(inst));
            }
            return out;
        },
        allUnits() {
            const out = [];
            for (const id of canonicalCellOrder) {
                const c = draft.board.cells[id];
                if (c === undefined || c.occupantId === null)
                    continue;
                const inst = draft.instances[c.occupantId];
                if (inst !== undefined)
                    out.push(toUnitView(inst));
            }
            return out;
        },
        hasCompletedAppearance(instanceId) {
            const instance = draft.instances[instanceId];
            return instance !== undefined
                && (instance.enteredRound !== draft.round || completedAppearanceIds.has(instanceId));
        },
        stats(unit) {
            return effectiveStats(draft, resolveId(unit));
        },
        player(playerId) {
            return toPlayerView(requirePlayer(draft, playerId, 'q.player'));
        },
        opponentOf(playerId) {
            requirePlayer(draft, playerId, 'q.opponentOf');
            const other = Object.values(draft.players).find((p) => p.id !== playerId);
            if (other === undefined) {
                throw new Error(`q.opponentOf: у игрока "${playerId}" нет оппонента (нужно два игрока)`);
            }
            return toPlayerView(other);
        },
        effectsOn(scope) {
            return draft.effects
                .filter((e) => scopeEquals(e.scope, scope))
                .sort(compareEffectsCanonical)
                .map(toEffectView);
        },
        rule(key) {
            return draft.rules[key];
        },
        hand(playerId) {
            return [...requirePlayer(draft, playerId, 'q.hand').hand];
        },
        attachmentsOn(hostInstanceId) {
            return draft.attachments
                .filter((attachment) => attachment.hostInstanceId === hostInstanceId)
                .sort((a, b) => a.seq - b.seq)
                .map((attachment) => structuredClone(attachment));
        },
        attachment(attachmentId) {
            const attachment = draft.attachments.find((candidate) => candidate.attachmentId === attachmentId);
            return attachment === undefined ? null : structuredClone(attachment);
        },
        captureReservation(instanceId) {
            const reservation = draft.captureReservations.find((candidate) => candidate.targetInstanceId === instanceId);
            return reservation === undefined ? null : structuredClone(reservation);
        },
        isCellReserved(cellId) {
            return draft.captureReservations.some((reservation) => reservation.sourceCellId === cellId);
        },
        hasStagedFieldInCell(playerId, cellId) {
            return requirePlayer(draft, playerId, 'q.hasStagedFieldInCell').staged
                .some((plan) => plan.kind === 'FIELD' && plan.cellId === cellId);
        },
        canAttack(instanceId, options) {
            const instance = draft.instances[instanceId];
            const definition = instance === undefined ? undefined : registry?.get(instance.defId);
            const canFormAttackIntent = definition?.type === 'UNIT' || draft.effects.some((effect) => effect.scope.kind === 'unit'
                && effect.scope.instanceId === instanceId
                && effect.vars.attackPermission === 'allow_non_unit');
            if (instance === undefined || definition === undefined
                || instance.zone !== 'board' || instance.cellId === undefined
                || !canFormAttackIntent)
                return false;
            if (effectiveStats(draft, instanceId).attack <= 0)
                return false;
            if (options?.ignoreSummoningDelay !== true
                && instance.enteredRound === draft.round
                && !definition.keywords?.includes('RUSH')
                && !ignoresSummoningDelay(draft, instanceId))
                return false;
            if ((instance.freeze ?? 0) > 0 || definition.keywords?.includes('CANNOT_ATTACK'))
                return false;
            if (hasUnitAttackPermission(draft, instanceId, 'prohibit'))
                return false;
            return true;
        },
    };
}
/** Структурное равенство scope (для q.effectsOn). */
function scopeEquals(a, b) {
    if (a.kind !== b.kind)
        return false;
    switch (a.kind) {
        case 'table':
            return b.kind === 'table' && a.tableId === b.tableId;
        case 'lane':
            return b.kind === 'lane' && a.tableId === b.tableId && a.laneIndex === b.laneIndex;
        case 'cell':
            return b.kind === 'cell' && a.cellId === b.cellId;
        case 'player':
            return b.kind === 'player' && a.playerId === b.playerId;
        case 'unit':
            return b.kind === 'unit' && a.instanceId === b.instanceId;
    }
}
/** RNG карты: читает draft.rng, продвигает его детерминированно (D7, tech/21 §8). */
function buildRng(draft) {
    return {
        int(minIncl, maxIncl) {
            const [v, next] = rngInt(draft.rng, minIncl, maxIncl);
            draft.rng = next;
            return v;
        },
        chance(p) {
            const [v, next] = rngChance(draft.rng, p);
            draft.rng = next;
            return v;
        },
        pick(xs) {
            const [v, next] = rngPick(draft.rng, xs);
            draft.rng = next;
            return v;
        },
        shuffle(xs) {
            const [v, next] = rngShuffle(draft.rng, xs);
            draft.rng = next;
            return v;
        },
    };
}
/** Ops карты поверх ядровых ops: авто-sourceId (D31) + setVar с авто-носителем. */
function buildCardOps(baseOps, autoSourceId, varTarget) {
    // Exact-cell createToken — внутренний строгий примитив. Карты получают только
    // safe tryCreateToken/createTokenInLocation, учитывающие committed reservations.
    const { createToken: unsafeCreateToken, ...safeOps } = baseOps;
    void unsafeCreateToken;
    return {
        ...safeOps,
        dealDamage(target, amount, sourceId) {
            // Авто-подстановка источника из контекста хука (D31), если не задан явно.
            baseOps.dealDamage(target, amount, sourceId ?? autoSourceId);
        },
        drawCard(playerId, n, sourceId) {
            baseOps.drawCard(playerId, n, sourceId ?? autoSourceId);
        },
        restorePlayerHealth(playerId, amount, sourceId) {
            baseOps.restorePlayerHealth(playerId, amount, sourceId ?? autoSourceId);
        },
        modifyStat(instanceId, stat, delta, sourceId) {
            baseOps.modifyStat(instanceId, stat, delta, sourceId ?? autoSourceId);
        },
        selectRandomUnit(candidateIds, sourceId) {
            return baseOps.selectRandomUnit(candidateIds, sourceId ?? autoSourceId);
        },
        applyHack(victimInstanceId, initiatorPlayerId, sourceInstanceId) {
            return baseOps.applyHack(victimInstanceId, initiatorPlayerId, sourceInstanceId ?? autoSourceId);
        },
        attachCard(hostInstanceId, vars) {
            if (autoSourceId === undefined)
                throw new Error('ctx.ops.attachCard: нет source card instance');
            return baseOps.attachCard(autoSourceId, hostInstanceId, vars);
        },
        queueExtraAttack(attackerId) {
            return baseOps.queueExtraAttack(attackerId, autoSourceId);
        },
        setVar(key, value) {
            if (varTarget === undefined) {
                throw new Error('ctx.ops.setVar: нет носителя (self/effect) для записи vars');
            }
            baseOps.setVar(varTarget, key, value);
        },
    };
}
/**
 * Собрать CardContext для носителя (self и/или effect) поверх живого черновика.
 * autoSourceId (D31): self.id, иначе effect.sourceId. varTarget: unit/effect.
 * `baseOps` уже связан с очередью шага (bindOps) — сюда очередь не нужна.
 */
export function buildCardContext(draft, baseOps, subject, plan, registry, completedAppearanceIds) {
    const self = subject.selfId !== undefined
        ? toUnitView(requireInstance(draft, subject.selfId, 'buildCardContext.self'))
        : undefined;
    let effectView;
    if (subject.effectId !== undefined) {
        const eff = draft.effects.find((e) => e.id === subject.effectId);
        if (eff === undefined) {
            throw new Error(`buildCardContext: неизвестный эффект "${subject.effectId}"`);
        }
        effectView = toEffectView(eff);
    }
    const attachmentView = subject.attachmentId === undefined
        ? undefined
        : draft.attachments.find((attachment) => attachment.attachmentId === subject.attachmentId);
    if (subject.attachmentId !== undefined && attachmentView === undefined) {
        throw new Error(`buildCardContext: неизвестный attachment "${subject.attachmentId}"`);
    }
    // Авто-sourceId (D31): юнит-носитель → его id; иначе источник эффекта.
    const autoSourceId = self?.id ?? effectView?.sourceId ?? attachmentView?.sourceCardInstanceId;
    // Носитель setVar: юнит либо эффект.
    const varTarget = subject.selfId !== undefined
        ? { kind: 'unit', instanceId: subject.selfId }
        : subject.effectId !== undefined
            ? { kind: 'effect', effectId: subject.effectId }
            : subject.attachmentId !== undefined
                ? { kind: 'attachment', attachmentId: subject.attachmentId }
                : undefined;
    return {
        ...(self !== undefined ? { self } : {}),
        ...(effectView !== undefined ? { effect: effectView } : {}),
        ...(attachmentView !== undefined ? { attachment: structuredClone(attachmentView) } : {}),
        round: draft.round,
        ...(plan !== undefined ? { plan: withLegacyPlanProjection(plan) } : {}),
        q: buildQuery(draft, registry, completedAppearanceIds),
        ops: buildCardOps(baseOps, autoSourceId, varTarget),
        rng: buildRng(draft),
        // Маркер выбора: возвращает запрос как есть — его yield-ит генератор, а движок
        // читает запрос (дескриптор + filter) и приостанавливает шаг (tech/21 §5.1).
        choose: ((req) => req),
    };
}
/** Build the restricted synchronous interceptor projection over the live draft. */
export function buildSynchronousInterceptorContext(draft, baseOps, subject, registry) {
    const ctx = buildCardContext(draft, baseOps, subject, undefined, registry);
    return {
        ...(ctx.self !== undefined ? { self: ctx.self } : {}),
        ...(ctx.effect !== undefined ? { effect: ctx.effect } : {}),
        ...(ctx.attachment !== undefined ? { attachment: ctx.attachment } : {}),
        round: ctx.round,
        q: ctx.q,
        rng: ctx.rng,
        ops: {
            setVar: ctx.ops.setVar,
            addEffect: ctx.ops.addEffect,
            dealDamage: ctx.ops.dealDamage,
            moveUnit: ctx.ops.moveUnit,
            modifyEffectCounter: ctx.ops.modifyEffectCounter,
            removeEffect: ctx.ops.removeEffect,
        },
    };
}
/**
 * Собрать ЧИСТЫЙ контекст (PureContext, S9) для носителя (self и/или effect):
 * только чтение (self/effect/round/q), БЕЗ ops/rng/choose. Используется ядром
 * для вызова чистых modify*, replace*-хуков (tech/22 §3.1) — так их чистота
 * гарантирована типом, и им нечем нарушить одновременность боя (D2).
 */
export function buildPureContext(draft, subject, registry) {
    const self = subject.selfId !== undefined
        ? toUnitView(requireInstance(draft, subject.selfId, 'buildPureContext.self'))
        : undefined;
    let effectView;
    if (subject.effectId !== undefined) {
        const eff = draft.effects.find((e) => e.id === subject.effectId);
        if (eff === undefined) {
            throw new Error(`buildPureContext: неизвестный эффект "${subject.effectId}"`);
        }
        effectView = toEffectView(eff);
    }
    const attachmentView = subject.attachmentId === undefined
        ? undefined
        : draft.attachments.find((attachment) => attachment.attachmentId === subject.attachmentId);
    if (subject.attachmentId !== undefined && attachmentView === undefined) {
        throw new Error(`buildPureContext: неизвестный attachment "${subject.attachmentId}"`);
    }
    return {
        ...(self !== undefined ? { self } : {}),
        ...(effectView !== undefined ? { effect: effectView } : {}),
        ...(attachmentView !== undefined ? { attachment: structuredClone(attachmentView) } : {}),
        round: draft.round,
        q: buildQuery(draft, registry),
    };
}
//# sourceMappingURL=context.js.map